import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import { toast } from "react-toastify";
import "./styles/LoginRegister.css";
import "../styles/global.css";
import { consumeRedirect } from "../utils/authRedirect";

const initialFormData = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  terms: false,
};

// ─────────────────────────────────────────────
// VALIDATION HELPERS (shared)
// ─────────────────────────────────────────────
const validators = {
  name: (v) => {
    if (!v || !v.trim()) return "Full name is required";
    if (v.trim().length < 2) return "Name must be at least 2 characters";
    if (!/^[a-zA-Z\s.'-]+$/.test(v.trim())) return "Name can only contain letters";
    return "";
  },
  email: (v) => {
    if (!v || !v.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Enter a valid email address";
    return "";
  },
  emailOrMobile: (v) => {
    if (!v || !v.trim()) return "Email or mobile is required";
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
    const isMobile = /^[+]?[\d\s-]{10,15}$/.test(v.trim());
    if (!isEmail && !isMobile) return "Enter a valid email or 10-digit mobile";
    return "";
  },
  mobile: (v) => {
    if (!v || !v.trim()) return "Mobile number is required";
    const digits = v.replace(/\D/g, "");
    if (digits.length < 10) return "Mobile must be at least 10 digits";
    if (digits.length > 15) return "Mobile is too long";
    return "";
  },
  password: (v) => {
    if (!v) return "Password is required";
    if (v.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(v)) return "Add at least one uppercase letter";
    if (!/[a-z]/.test(v)) return "Add at least one lowercase letter";
    if (!/[0-9]/.test(v)) return "Add at least one number";
    if (!/[^A-Za-z0-9]/.test(v)) return "Add at least one special character";
    return "";
  },
  loginPassword: (v) => {
    if (!v) return "Password is required";
    if (v.length < 6) return "Password must be at least 6 characters";
    return "";
  },
  terms: (v) => (!v ? "You must accept the terms" : ""),
  otp: (v) => {
    if (!v || !v.trim()) return "Please enter the OTP";
    if (!/^\d{6}$/.test(v.trim())) return "OTP must be 6 digits";
    return "";
  },
};

// Password strength score 0–5
const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  let label = "Weak";
  let color = "weak";
  if (score >= 4) {
    label = "Strong";
    color = "strong";
  } else if (score >= 3) {
    label = "Good";
    color = "good";
  } else if (score >= 2) {
    label = "Medium";
    color = "medium";
  }
  return { score, label, color };
};



const LoginRegister = () => {
  // ─── UI tab state ───
  const [activeTab, setActiveTab] = useState("login"); // "login" | "register"
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");

  // ─── Forgot password (OTP-based) state ───
  const [forgotStep, setForgotStep] = useState("request"); // "request" | "reset"
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotResetErrors, setForgotResetErrors] = useState({});

  // ─── Errors map (per field) ───
  const [errors, setErrors] = useState({});
  const [registerErrors, setRegisterErrors] = useState({});
  const [otpError, setOtpError] = useState("");
  const [otpInput, setOtpInput] = useState("");

  // ─── OTP Login Modal state ───
  const [showOtpLoginModal, setShowOtpLoginModal] = useState(false);
  const [otpLoginStep, setOtpLoginStep] = useState("mobile"); // "mobile" | "verify"
  const [otpLoginMobile, setOtpLoginMobile] = useState("");
  const [otpLoginCode, setOtpLoginCode] = useState("");
  const [otpLoginErrors, setOtpLoginErrors] = useState({});
  const [otpLoginSending, setOtpLoginSending] = useState(false);

  // ────────────────────────────────────────────────
  // ORIGINAL STATE (unchanged)
  // ────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Live-clear field error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ─── Run validation ───
    const newErrors = {
      email: validators.emailOrMobile(formData.email),
      password: validators.loginPassword(formData.password),
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some((v) => v)) {
      toast.error("Please fix the errors below");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        mode: "cors",
      });

      const data = await res.json();

      if (res.status) {
        if(data.status){
          localStorage.setItem("authToken", data.token);
          localStorage.setItem("config_id", data.config_id);
          localStorage.setItem("user_id", data.user.id);
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("name", data.user.name);
          localStorage.setItem("role", data.user.role);

          setResponseMessage("Login successful!");
          consumeRedirect(navigate, "/");
        }else{
          setResponseMessage(data.message);
        }
        
      } else {
        let errorMessage = "Something Went Wrong.";
        if (res.status === 400) errorMessage = "Bad request. Please check the input data.";
        else if (res.status === 401) errorMessage = "Unauthorized. Incorrect username or password.";
        else if (res.status === 403) errorMessage = "Forbidden. You don't have permission to access this.";
        else if (res.status === 500) errorMessage = "Server error. Please try again later.";
        setResponseMessage(errorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      setResponseMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [formRegisterData, setFormRegisterData] = useState(initialFormData);

  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormRegisterData({
      ...formRegisterData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (registerErrors[name]) {
      setRegisterErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    // ─── Run validation ───
    const newErrors = {
      name: validators.name(formRegisterData.name),
      email: validators.email(formRegisterData.email),
      mobile: validators.mobile(formRegisterData.mobile),
      password: validators.password(formRegisterData.password),
      terms: validators.terms(formRegisterData.terms),
    };
    setRegisterErrors(newErrors);
    if (Object.values(newErrors).some((v) => v)) {
      toast.error("Please fix the errors below");
      return;
    }

    setShowOtp(true);
  };

  const [showOtp, setShowOtp] = useState(false);
  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    // ─── Validate OTP ───
    const err = validators.otp(otpInput);
    if (err) {
      setOtpError(err);
      return;
    }
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}api/appRegister`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formRegisterData),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("Server error:", text);
        throw new Error("API Error");
      }

      const data = await response.json();
      if (data.status) {
        setFormRegisterData(initialFormData);
        setOtpInput("");
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
      toast.error("Fetch failed – check API URL / CORS");
    }
  };

  // ─── Forgot password (OTP-based, backed by Laravel API) ───

  // Step 1: request OTP
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const err = validators.emailOrMobile(forgotEmail);
    if (err) {
      setForgotError(err);
      return;
    }
    setForgotError("");
    setForgotSending(true);

    try {
      const res = await fetch(`${API_BASE_URL}api/forgotPassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();

      if (data.status) {
        toast.success(data.message);
        setForgotStep("reset");
      } else {
        setForgotError(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setForgotError("Network error. Please try again.");
    } finally {
      setForgotSending(false);
    }
  };

  // Step 2: submit OTP + new password
  const handleForgotResetSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      otp: validators.otp(forgotOtp),
      password: validators.password(forgotNewPassword),
      confirmPassword:
        forgotNewPassword !== forgotConfirmPassword ? "Passwords do not match" : "",
    };
    setForgotResetErrors(newErrors);
    if (Object.values(newErrors).some((v) => v)) return;

    setForgotSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/resetPasswordOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotEmail,
          otp: forgotOtp,
          password: forgotNewPassword,
        }),
      });
      const data = await res.json();

      if (data.status) {
        toast.success(data.message);
        closeForgotModal();
        setActiveTab("login");
      } else {
        setForgotResetErrors({ otp: data.message });
      }
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error("Network error. Please try again.");
    } finally {
      setForgotSending(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgot(false);
    setForgotStep("request");
    setForgotEmail("");
    setForgotError("");
    setForgotOtp("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setForgotResetErrors({});
  };

  // ─── Google sign-in placeholder ───
  const handleGoogleLogin = () => {
  const client = window.google.accounts.oauth2.initTokenClient({
    client_id: "137754741246-jiflpefl25dmop64hbbalcmq8nlffe18.apps.googleusercontent.com",
    scope: "openid email profile",
    callback: async (tokenResponse) => {
      if (tokenResponse.error) {
        toast.error("Google sign-in failed");
        return;
      }
      // Send token to your Laravel backend
      try {
        const res = await fetch(`${API_BASE_URL}api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });
        const data = await res.json();
        if (data.status) {
          localStorage.setItem("authToken", data.token);
          localStorage.setItem("config_id", data.config_id);
          localStorage.setItem("user_id", data.user.id);
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("name", data.user.name);
          localStorage.setItem("role", data.user.role);
          consumeRedirect(navigate, "/");
        } else {
          toast.error(data.message || "Google login failed");
        }
      } catch (err) {
        toast.error("An error occurred during Google login");
      }
    },
  });
  client.requestAccessToken();
};


  // ─── OTP Login Modal handlers ───
  const openOtpLogin = () => {
    setShowOtpLoginModal(true);
    setOtpLoginStep("mobile");
    setOtpLoginMobile("");
    setOtpLoginCode("");
    setOtpLoginErrors({});
  };

  const closeOtpLogin = () => {
    setShowOtpLoginModal(false);
    setOtpLoginStep("mobile");
    setOtpLoginMobile("");
    setOtpLoginCode("");
    setOtpLoginErrors({});
  };

  const handleOtpLoginSendCode = (e) => {
    e.preventDefault();
    const err = validators.mobile(otpLoginMobile);
    if (err) {
      setOtpLoginErrors({ mobile: err });
      return;
    }
    setOtpLoginErrors({});
    setOtpLoginSending(true);

    // Simulate sending OTP — replace with real API when ready
    setTimeout(() => {
      setOtpLoginSending(false);
      setOtpLoginStep("verify");
      toast.success(`OTP sent to ${otpLoginMobile}`);
    }, 800);
  };

  const handleOtpLoginVerify = (e) => {
    e.preventDefault();
    const err = validators.otp(otpLoginCode);
    if (err) {
      setOtpLoginErrors({ code: err });
      return;
    }
    setOtpLoginErrors({});
    setOtpLoginSending(true);

    // Simulate verify — replace with real API when ready
    setTimeout(() => {
      setOtpLoginSending(false);
      toast.success("Logged in successfully");
      closeOtpLogin();
      // navigate("/"); // uncomment when wired to real API
    }, 800);
  };

  const handleOtpLoginResend = () => {
    toast.success(`New OTP sent to ${otpLoginMobile}`);
  };

  // Password strength
  const pwStrength = getPasswordStrength(formRegisterData.password);

  return (
    <>
      <Header />

      <section className="auth-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-11 col-xl-10">
              <div className="auth-card">
                {loading && (
                  <div className="auth-loader">
                    <div className="auth-spinner"></div>
                  </div>
                )}

                <div className="auth-grid">
                  {/* ═══════════ LEFT VISUAL PANEL ═══════════ */}
                  <div className="auth-visual d-md-block d-none">
                    <div className="auth-brand">
                      <div className="auth-brand-mark">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
                        </svg>
                      </div>
                        ACEVEDA
                    </div>

                    <div className="auth-visual-content">
                      <h1>Your health, <span>simplified.</span></h1>
                      <p>
                        Book appointments, consult doctors online, and manage your medical
                        records — all in one secure platform.
                      </p>

                      <div className="auth-features">
                        <div className="auth-feature">
                          <div className="auth-feature-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M23 7l-7 5 7 5V7z" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          </div>
                          <div>
                            <div className="auth-feature-text">Video Consultations</div>
                            <div className="auth-feature-sub">Talk to doctors from home</div>
                          </div>
                        </div>
                        <div className="auth-feature">
                          <div className="auth-feature-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </div>
                          <div>
                            <div className="auth-feature-text">Easy Scheduling</div>
                            <div className="auth-feature-sub">Book in under 60 seconds</div>
                          </div>
                        </div>
                        <div className="auth-feature">
                          <div className="auth-feature-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                          </div>
                          <div>
                            <div className="auth-feature-text">Secure & Private</div>
                            <div className="auth-feature-sub">End-to-end encrypted</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="auth-stats">
                      <div><p className="auth-stat-num">500+</p><p className="auth-stat-lbl">Doctors</p></div>
                      <div><p className="auth-stat-num">6L+</p><p className="auth-stat-lbl">Patients</p></div>
                      <div><p className="auth-stat-num">4.9★</p><p className="auth-stat-lbl">Rating</p></div>
                    </div>
                  </div>

                  {/* ═══════════ RIGHT FORM PANEL ═══════════ */}
                  <div className="auth-form-wrap">
                    <div className="auth-tabs">
                      <button
                        type="button"
                        className={`auth-tab ${activeTab === "login" ? "is-active" : ""}`}
                        onClick={() => { setActiveTab("login"); setShowOtp(false); }}
                      >
                        Sign In
                      </button>
                      <button
                        type="button"
                        className={`auth-tab ${activeTab === "register" ? "is-active" : ""}`}
                        onClick={() => setActiveTab("register")}
                      >
                        Create Account
                      </button>
                    </div>

                    {/* ───── LOGIN TAB ───── */}
                    {activeTab === "login" && (
                      <>
                        <h2 className="auth-title">Welcome back</h2>
                        <p className="auth-subtitle">Sign in to access your health dashboard</p>

                        <div className="auth-doctor-link">
                          <span>👨‍⚕️ Are you a Doctor?</span>
                          <Link to="https://sivakasicrackerscity.com/shreevarma/doctoradmin/">
                            Login here →
                          </Link>
                        </div>

                        <form onSubmit={handleSubmit} noValidate>
                          {responseMessage && (
                            <div className="auth-alert">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                              {responseMessage}
                            </div>
                          )}

                          <div className="auth-field">
                            <label className="auth-label">Mobile Number / Email</label>
                            <div className="auth-input-wrap">
                              <input
                                type="text"
                                className={`auth-input ${errors.email ? "has-error" : ""}`}
                                placeholder="you@example.com"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                autoComplete="email"
                                autoFocus
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                  <polyline points="22,6 12,13 2,6" />
                                </svg>
                              </span>
                            </div>
                            {errors.email && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="8" x2="12" y2="12" />
                                  <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                {errors.email}
                              </div>
                            )}
                          </div>

                          <div className="auth-field">
                            <label className="auth-label">Password</label>
                            <div className="auth-input-wrap">
                              <input
                                type={showPassword ? "text" : "password"}
                                className={`auth-input ${errors.password ? "has-error" : ""}`}
                                placeholder="Enter your password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                autoComplete="current-password"
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                              </span>
                              <button
                                type="button"
                                className="auth-pw-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                )}
                              </button>
                            </div>
                            {errors.password && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="8" x2="12" y2="12" />
                                  <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                {errors.password}
                              </div>
                            )}
                          </div>

                          <div className="auth-row">
                            <label className="auth-check">
                              <input type="checkbox" />
                              Remember me
                            </label>
                            <button
                              type="button"
                              className="auth-link"
                              onClick={() => { setShowForgot(true); setForgotStep("request"); }}
                            >
                              Forgot password?
                            </button>
                          </div>

                          {/* OTP login trigger — opens modal */}
                          <div className="auth-otp-note" onClick={openOtpLogin} role="button">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                              <line x1="12" y1="18" x2="12.01" y2="18" />
                            </svg>
                            <div className="auth-otp-note-text">
                              <p className="auth-otp-note-title">Login with OTP instead</p>
                              <p className="auth-otp-note-sub">No password needed — get a code on your mobile</p>
                            </div>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </div>

                          <button type="submit" className="auth-btn auth-btn--primary" disabled={loading}>
                            {loading ? "Signing in..." : "Sign In"}
                            {!loading && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            )}
                          </button>
                        </form>

                        <div className="auth-divider">or continue with</div>

                        <button type="button" className="auth-btn auth-btn--google" onClick={handleGoogleLogin}>
                          <svg width="20" height="20" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                          </svg>
                          Continue with Google
                        </button>

                        <p className="auth-foot-note">
                          Don't have an account?{" "}
                          <button onClick={() => setActiveTab("register")}>Sign up free</button>
                        </p>
                      </>
                    )}

                    {/* ───── REGISTER TAB ───── */}
                    {activeTab === "register" && !showOtp && (
                      <>
                        <h2 className="auth-title">Create your account</h2>
                        <p className="auth-subtitle">Get started with personalized health care</p>

                        <div className="auth-doctor-link">
                          <span>👨‍⚕️ Are you a Doctor?</span>
                          <Link to="/doctor-register">Register here →</Link>
                        </div>

                        <form onSubmit={handleRegisterSubmit} noValidate>
                          <div className="auth-field">
                            <label className="auth-label">Full Name</label>
                            <div className="auth-input-wrap">
                              <input
                                type="text"
                                className={`auth-input ${registerErrors.name ? "has-error" : ""}`}
                                name="name"
                                placeholder="John Doe"
                                value={formRegisterData.name}
                                onChange={handleRegisterChange}
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                              </span>
                            </div>
                            {registerErrors.name && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {registerErrors.name}
                              </div>
                            )}
                          </div>

                          <div className="auth-field">
                            <label className="auth-label">Email Address</label>
                            <div className="auth-input-wrap">
                              <input
                                type="email"
                                className={`auth-input ${registerErrors.email ? "has-error" : ""}`}
                                name="email"
                                placeholder="you@example.com"
                                value={formRegisterData.email}
                                onChange={handleRegisterChange}
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                  <polyline points="22,6 12,13 2,6" />
                                </svg>
                              </span>
                            </div>
                            {registerErrors.email && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {registerErrors.email}
                              </div>
                            )}
                          </div>

                          <div className="auth-field">
                            <label className="auth-label">Mobile Number</label>
                            <div className="auth-input-wrap">
                              <input
                                type="text"
                                className={`auth-input ${registerErrors.mobile ? "has-error" : ""}`}
                                name="mobile"
                                placeholder="+91 00000 00000"
                                value={formRegisterData.mobile}
                                onChange={handleRegisterChange}
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                              </span>
                            </div>
                            {registerErrors.mobile && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {registerErrors.mobile}
                              </div>
                            )}
                          </div>

                          <div className="auth-field">
                            <label className="auth-label">Create Password</label>
                            <div className="auth-input-wrap">
                              <input
                                type={showPassword ? "text" : "password"}
                                className={`auth-input ${registerErrors.password ? "has-error" : ""}`}
                                name="password"
                                placeholder="Minimum 8 characters"
                                value={formRegisterData.password}
                                onChange={handleRegisterChange}
                              />
                              <span className="auth-input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                              </span>
                              <button
                                type="button"
                                className="auth-pw-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                )}
                              </button>
                            </div>

                            {/* Password strength meter */}
                            {formRegisterData.password && (
                              <div className="auth-pw-strength-wrap">
                                <div className="auth-pw-strength">
                                  <div className={`auth-pw-bar ${pwStrength.score >= 1 ? `is-${pwStrength.color}` : ""}`}></div>
                                  <div className={`auth-pw-bar ${pwStrength.score >= 2 ? `is-${pwStrength.color}` : ""}`}></div>
                                  <div className={`auth-pw-bar ${pwStrength.score >= 3 ? `is-${pwStrength.color}` : ""}`}></div>
                                  <div className={`auth-pw-bar ${pwStrength.score >= 4 ? `is-${pwStrength.color}` : ""}`}></div>
                                  <div className={`auth-pw-bar ${pwStrength.score >= 5 ? `is-${pwStrength.color}` : ""}`}></div>
                                </div>
                                <div className="auth-pw-meta">
                                  <span style={{ color: "var(--auth-muted)" }}>Password strength:</span>
                                  <span className={`auth-pw-label is-${pwStrength.color}`}>{pwStrength.label}</span>
                                </div>
                                <div className="auth-pw-checks">
                                  <span className={`auth-pw-check ${formRegisterData.password.length >= 8 ? "is-met" : ""}`}>
                                    <span className="auth-pw-check-dot">{formRegisterData.password.length >= 8 && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}</span>
                                    8+ characters
                                  </span>
                                  <span className={`auth-pw-check ${/[A-Z]/.test(formRegisterData.password) ? "is-met" : ""}`}>
                                    <span className="auth-pw-check-dot">{/[A-Z]/.test(formRegisterData.password) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}</span>
                                    Uppercase letter
                                  </span>
                                  <span className={`auth-pw-check ${/[a-z]/.test(formRegisterData.password) ? "is-met" : ""}`}>
                                    <span className="auth-pw-check-dot">{/[a-z]/.test(formRegisterData.password) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}</span>
                                    Lowercase letter
                                  </span>
                                  <span className={`auth-pw-check ${/[0-9]/.test(formRegisterData.password) ? "is-met" : ""}`}>
                                    <span className="auth-pw-check-dot">{/[0-9]/.test(formRegisterData.password) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}</span>
                                    Number
                                  </span>
                                  <span className={`auth-pw-check ${/[^A-Za-z0-9]/.test(formRegisterData.password) ? "is-met" : ""}`}>
                                    <span className="auth-pw-check-dot">{/[^A-Za-z0-9]/.test(formRegisterData.password) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}</span>
                                    Special character
                                  </span>
                                </div>
                              </div>
                            )}

                            {registerErrors.password && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {registerErrors.password}
                              </div>
                            )}
                          </div>

                          <label className="auth-terms">
                            <input
                              type="checkbox"
                              name="terms"
                              checked={formRegisterData.terms}
                              onChange={handleRegisterChange}
                            />
                            <span>
                              I agree to receive offers & promotional communication, and accept the{" "}
                              <a href="#">Terms & Conditions</a> and <a href="#">Privacy Policy</a>.
                            </span>
                          </label>
                          {registerErrors.terms && (
                            <div className="auth-error" style={{ marginTop: -4, marginBottom: 14 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                              {registerErrors.terms}
                            </div>
                          )}

                          <button type="submit" className="auth-btn auth-btn--primary">
                            Create Account
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                          </button>
                        </form>

                        <div className="auth-divider">or sign up with</div>

                        <button type="button" className="auth-btn auth-btn--google" onClick={handleGoogleLogin}>
                          <svg width="20" height="20" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                          </svg>
                          Continue with Google
                        </button>

                        <p className="auth-foot-note">
                          Already have an account?{" "}
                          <button onClick={() => setActiveTab("login")}>Sign in</button>
                        </p>
                      </>
                    )}

                    {/* ───── REGISTER OTP STEP ───── */}
                    {activeTab === "register" && showOtp && (
                      <>
                        <div className="auth-otp-head">
                          <div className="auth-otp-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="3" />
                              <path d="M7 9l5 4 5-4" />
                            </svg>
                          </div>
                          <h3 className="auth-otp-title">Verify your number</h3>
                          <p className="auth-otp-sub">
                            We sent a 6-digit code to <b>{formRegisterData.mobile || "your mobile"}</b>
                            <button
                              type="button"
                              className="auth-otp-edit"
                              onClick={() => { setShowOtp(false); setOtpInput(""); setOtpError(""); }}
                            >
                              Edit
                            </button>
                          </p>
                        </div>

                        <form onSubmit={handleOtpSubmit} noValidate>
                          <div className="auth-field">
                            <label className="auth-label">Enter OTP</label>
                            <div className="auth-input-wrap">
                              <input
                                type="text"
                                inputMode="numeric"
                                className={`auth-input ${otpError ? "has-error" : ""}`}
                                placeholder="• • • • • •"
                                maxLength={6}
                                value={otpInput}
                                onChange={(e) => {
                                  const v = e.target.value.replace(/\D/g, "");
                                  setOtpInput(v);
                                  if (otpError) setOtpError("");
                                }}
                                style={{
                                  letterSpacing: "0.4em",
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: 18,
                                  paddingLeft: 14,
                                }}
                              />
                            </div>
                            {otpError && (
                              <div className="auth-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {otpError}
                              </div>
                            )}
                          </div>

                          <div className="auth-otp-resend">
                            <span>
                              Didn't receive code?{" "}
                              <button type="button" className="auth-link">Get via call</button>
                            </span>
                            <button type="button" className="auth-link">Resend OTP</button>
                          </div>

                          <button type="submit" className="auth-btn auth-btn--primary">
                            Complete Registration
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </button>
                        </form>

                        <p className="auth-foot-note">
                          <button onClick={() => { setShowOtp(false); setOtpInput(""); setOtpError(""); }}>
                            ← Back to registration
                          </button>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FORGOT PASSWORD MODAL (OTP-based) ═══════ */}
      {showForgot && (
        <div className="auth-modal-bg" onClick={closeForgotModal}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="auth-modal-close" onClick={closeForgotModal}>✕</button>

            {forgotStep === "request" && (
              <>
                <div className="auth-modal-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                </div>
                <h4>Forgot password?</h4>
                <p className="auth-modal-desc">
                  No worries — enter your email and we'll send you a 6-digit OTP.
                </p>
                <form onSubmit={handleForgotSubmit} noValidate>
                  <div className="auth-field">
                    <label className="auth-label">Email or Mobile</label>
                    <div className="auth-input-wrap">
                      <input
                        type="text"
                        className={`auth-input ${forgotError ? "has-error" : ""}`}
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => { setForgotEmail(e.target.value); if (forgotError) setForgotError(""); }}
                        autoFocus
                      />
                      <span className="auth-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                    </div>
                    {forgotError && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {forgotError}
                      </div>
                    )}
                  </div>
                  <div className="auth-modal-actions">
                    <button type="button" className="auth-btn auth-btn--ghost" onClick={closeForgotModal}>
                      Cancel
                    </button>
                    <button type="submit" className="auth-btn auth-btn--primary" disabled={forgotSending}>
                      {forgotSending ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                </form>
              </>
            )}

            {forgotStep === "reset" && (
              <>
                <div className="auth-modal-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="3" />
                    <path d="M7 9l5 4 5-4" />
                  </svg>
                </div>
                <h4>Reset your password</h4>
                <p className="auth-modal-desc">
                  Enter the OTP sent to <b>{forgotEmail}</b>{" "}
                  <button type="button" className="auth-otp-edit" onClick={() => setForgotStep("request")}>
                    Change
                  </button>
                </p>

                <form onSubmit={handleForgotResetSubmit} noValidate>
                  <div className="auth-field">
                    <label className="auth-label">Enter OTP</label>
                    <div className="auth-input-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`auth-input ${forgotResetErrors.otp ? "has-error" : ""}`}
                        placeholder="• • • • • •"
                        maxLength={6}
                        value={forgotOtp}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "");
                          setForgotOtp(v);
                          if (forgotResetErrors.otp) setForgotResetErrors((prev) => ({ ...prev, otp: "" }));
                        }}
                        style={{ letterSpacing: "0.4em", textAlign: "center", fontWeight: 700, fontSize: 18, paddingLeft: 14 }}
                        autoFocus
                      />
                    </div>
                    {forgotResetErrors.otp && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {forgotResetErrors.otp}
                      </div>
                    )}
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">New Password</label>
                    <div className="auth-input-wrap">
                      <input
                        type="password"
                        className={`auth-input ${forgotResetErrors.password ? "has-error" : ""}`}
                        placeholder="Minimum 8 characters"
                        value={forgotNewPassword}
                        onChange={(e) => {
                          setForgotNewPassword(e.target.value);
                          if (forgotResetErrors.password) setForgotResetErrors((prev) => ({ ...prev, password: "" }));
                        }}
                      />
                    </div>
                    {forgotResetErrors.password && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {forgotResetErrors.password}
                      </div>
                    )}
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">Confirm New Password</label>
                    <div className="auth-input-wrap">
                      <input
                        type="password"
                        className={`auth-input ${forgotResetErrors.confirmPassword ? "has-error" : ""}`}
                        placeholder="Re-enter new password"
                        value={forgotConfirmPassword}
                        onChange={(e) => {
                          setForgotConfirmPassword(e.target.value);
                          if (forgotResetErrors.confirmPassword) setForgotResetErrors((prev) => ({ ...prev, confirmPassword: "" }));
                        }}
                      />
                    </div>
                    {forgotResetErrors.confirmPassword && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {forgotResetErrors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <div className="auth-otp-resend">
                    <span>Didn't receive code?</span>
                    <button type="button" className="auth-link" onClick={handleForgotSubmit}>
                      Resend OTP
                    </button>
                  </div>

                  <div className="auth-modal-actions">
                    <button type="button" className="auth-btn auth-btn--ghost" onClick={() => setForgotStep("request")}>
                      Back
                    </button>
                    <button type="submit" className="auth-btn auth-btn--primary" disabled={forgotSending}>
                      {forgotSending ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════ OTP LOGIN MODAL ═══════ */}
      {showOtpLoginModal && (
        <div className="auth-modal-bg" onClick={closeOtpLogin}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="auth-modal-close" onClick={closeOtpLogin}>✕</button>

            <div className="auth-modal-icon">
              {otpLoginStep === "mobile" ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4" />
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              )}
            </div>

            {otpLoginStep === "mobile" && (
              <>
                <h4>Login with OTP</h4>
                <p className="auth-modal-desc">
                  Enter your registered mobile number and we'll send a verification code.
                </p>

                <form onSubmit={handleOtpLoginSendCode} noValidate>
                  <div className="auth-field">
                    <label className="auth-label">Mobile Number</label>
                    <div className="auth-input-wrap">
                      <input
                        type="tel"
                        inputMode="numeric"
                        className={`auth-input ${otpLoginErrors.mobile ? "has-error" : ""}`}
                        placeholder="+91 00000 00000"
                        value={otpLoginMobile}
                        onChange={(e) => {
                          setOtpLoginMobile(e.target.value);
                          if (otpLoginErrors.mobile) setOtpLoginErrors({});
                        }}
                        autoFocus
                      />
                      <span className="auth-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                    </div>
                    {otpLoginErrors.mobile && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {otpLoginErrors.mobile}
                      </div>
                    )}
                  </div>

                  <div className="auth-modal-actions">
                    <button type="button" className="auth-btn auth-btn--ghost" onClick={closeOtpLogin}>
                      Cancel
                    </button>
                    <button type="submit" className="auth-btn auth-btn--primary" disabled={otpLoginSending}>
                      {otpLoginSending ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                </form>
              </>
            )}

            {otpLoginStep === "verify" && (
              <>
                <h4>Enter verification code</h4>
                <p className="auth-modal-desc">
                  We sent a 6-digit code to <b>{otpLoginMobile}</b>
                  <button type="button" className="auth-otp-edit" onClick={() => setOtpLoginStep("mobile")}>
                    Change
                  </button>
                </p>

                <form onSubmit={handleOtpLoginVerify} noValidate>
                  <div className="auth-field">
                    <label className="auth-label">OTP Code</label>
                    <div className="auth-input-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`auth-input ${otpLoginErrors.code ? "has-error" : ""}`}
                        placeholder="• • • • • •"
                        maxLength={6}
                        value={otpLoginCode}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "");
                          setOtpLoginCode(v);
                          if (otpLoginErrors.code) setOtpLoginErrors({});
                        }}
                        autoFocus
                        style={{
                          letterSpacing: "0.4em",
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: 18,
                          paddingLeft: 14,
                        }}
                      />
                    </div>
                    {otpLoginErrors.code && (
                      <div className="auth-error">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {otpLoginErrors.code}
                      </div>
                    )}
                  </div>

                  <div className="auth-otp-resend">
                    <span>Didn't receive code?</span>
                    <button type="button" className="auth-link" onClick={handleOtpLoginResend}>
                      Resend OTP
                    </button>
                  </div>

                  <div className="auth-modal-actions">
                    <button type="button" className="auth-btn auth-btn--ghost" onClick={() => setOtpLoginStep("mobile")}>
                      Back
                    </button>
                    <button type="submit" className="auth-btn auth-btn--primary" disabled={otpLoginSending}>
                      {otpLoginSending ? "Verifying..." : "Verify & Login"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default LoginRegister;